import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Building2, Mail, ExternalLink } from 'lucide-react';
import { fetchProviders, createProvider, updateProvider, deleteProvider } from '../api/providers';
import Modal from '../components/Modal';
import { PageHeader } from '../components/ui/PageHeader';

function getQualityColor(score) {
  if (score == null) return 'text-muted-foreground';
  if (score >= 70) return 'text-success';
  if (score >= 40) return 'text-warning';
  return 'text-destructive';
}

export default function Providers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_email: '', notes: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['providers'],
    queryFn: fetchProviders,
  });

  const providers = data?.providers || [];

  const createMut = useMutation({
    mutationFn: createProvider,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['providers'] }); setShowModal(false); resetForm(); },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateProvider(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['providers'] }); setShowModal(false); resetForm(); },
  });

  const deleteMut = useMutation({
    mutationFn: deleteProvider,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['providers'] }),
  });

  function resetForm() {
    setForm({ name: '', contact_email: '', notes: '' });
    setEditing(null);
  }

  function openCreate() {
    resetForm();
    setShowModal(true);
  }

  function openEdit(provider) {
    setEditing(provider);
    setForm({ name: provider.name, contact_email: provider.contact_email || '', notes: provider.notes || '' });
    setShowModal(true);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form });
    } else {
      createMut.mutate(form);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Providers"
        subtitle="Manage link building agencies and vendors"
        actions={
          <button onClick={openCreate} className="btn-primary flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
            <Plus className="h-4 w-4" /> Add Provider
          </button>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="glow-card-filled rounded-xl p-5 animate-pulse h-40" />
          ))}
        </div>
      ) : providers.length === 0 ? (
        <div className="glow-card-filled rounded-xl p-12 text-center">
          <Building2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Providers Yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Track your link building agencies and vendors to monitor their performance.</p>
          <button onClick={openCreate} className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
            Add Your First Provider
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {providers.map(provider => {
            const s = provider.stats;
            return (
              <div
                key={provider.id}
                className="glow-card-filled rounded-xl p-5 cursor-pointer hover:ring-1 hover:ring-primary/20 transition-all group"
                onClick={() => navigate(`/providers/${provider.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                      {provider.name}
                    </h3>
                    {provider.contact_email && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Mail className="h-3 w-3" /> {provider.contact_email}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEdit(provider); }}
                      className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (confirm('Delete this provider?')) deleteMut.mutate(provider.id); }}
                      className="p-1.5 rounded hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-muted-foreground">Links</span>
                    <p className="text-foreground font-semibold text-base">{s.total_links}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Live</span>
                    <p className={`font-semibold text-base ${s.live_pct >= 80 ? 'text-success' : s.live_pct >= 50 ? 'text-warning' : 'text-destructive'}`}>
                      {s.live_pct}%
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Avg Quality</span>
                    <p className={`font-semibold text-base ${getQualityColor(s.avg_quality)}`}>
                      {s.avg_quality != null ? s.avg_quality.toFixed(0) : '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Spend</span>
                    <p className="text-foreground font-semibold text-base">
                      {s.total_cost > 0 ? `$${s.total_cost.toLocaleString()}` : '—'}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground group-hover:text-primary transition-colors">
                  View Scorecard <ExternalLink className="h-3 w-3" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal onClose={() => { setShowModal(false); resetForm(); }} title={editing ? 'Edit Provider' : 'Add Provider'}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                placeholder="e.g. LinkBuilding Agency"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Contact Email</label>
              <input
                type="email"
                value={form.contact_email}
                onChange={e => setForm({ ...form, contact_email: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                placeholder="contact@agency.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none resize-none"
                rows={3}
                placeholder="Contract details, pricing notes..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMut.isPending || updateMut.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {createMut.isPending || updateMut.isPending ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
